import { AnyAction } from 'redux'
import { createSelector } from 'reselect'
import { CONTENT_UPDATE, SIMILAR_TEMPLATES_INJECT, SIMILAR_TEMPLATES_RESET } from '~/app/store/constants'
import { getQuantitySelector, getLocaleSelector, stringRenderPropertySelector } from '~/app/store/config'
import { RenderProperty } from 'shared/renderProperties'
import { PREVIEW_TYPE } from 'shared/constants'
import { tileEntityByIdSelector } from '~/app/store/tileEntity'
import { getPreviewData } from '~/app/utils/previewData'
import { buildFullyQualifiedVistaprintUrl } from '~/app/utils/vistaprintUrlBuilder'
import { getLogger } from 'shared/logger'
import { similarDesignByIdSelector, similarTemplatesByIdSelector } from '~/app/store/similarTemplates/reducer'
import { getDefaultColorSwatch } from '~/app/store/design/utils'

const initialState: State.DesignState = {
  byId: {},
  allIds: [],
}

const updateState = (action: AnyAction, state: State.DesignState = initialState): State.DesignState => ({
  ...state,
  byId: action.payload.designs.byId,
  allIds: action.payload.designs.allIds,
})

export function reducer(state: State.DesignState = initialState, action: AnyAction): State.DesignState {
  switch (action.type) {
    case CONTENT_UPDATE:
      return updateState(action, state)
    case SIMILAR_TEMPLATES_INJECT:
      return {
        ...state,
        byIdSimilarTemplates: {
          ...state.byIdSimilarTemplates,
          ...action.payload.designs.byId,
        },
      }
    case SIMILAR_TEMPLATES_RESET:
      delete state.byIdSimilarTemplates
      return state
    default:
      return state
  }
}

export const designByIdSelector = createSelector(
  (state: State.GlobalState) => state.designs,
  (designs) =>
    (id: string): State.Design =>
      ({ ...designs.byId, ...designs.byIdSimilarTemplates })[id],
)

export const renderablePreviewInfoSelector = createSelector(
  stringRenderPropertySelector,
  designByIdSelector,
  (stringRenderProperty, designById) =>
    (
      currentDesignId: string,
      previewType: PREVIEW_TYPE = PREVIEW_TYPE.TILE,
      previewInfoProp?: Gallery.ContentQuery.PreviewInfo,
    ): Gallery.Designs.RenderablePreviewInfo => {
      const previewInfo = previewInfoProp ?? designById(currentDesignId).previewInfo
      const { previewUrls } = designById(currentDesignId)
      const tilePreviewSize = stringRenderProperty(RenderProperty.TilePreviewSize)
      const imageCacheVersion = stringRenderProperty(RenderProperty.ImageCacheVersion)

      return getPreviewData(previewInfo, previewType, previewUrls, tilePreviewSize, imageCacheVersion)
    },
)

export const colorSwatchSelector = createSelector(
  renderablePreviewInfoSelector,
  designByIdSelector,
  tileEntityByIdSelector,
  (renderablePreviewInfo, designById, tileEntityById) =>
    (designId: string, entityId: string, previewType: PREVIEW_TYPE): Gallery.Designs.CurrentColorSwatch => {
      const renderablePreviews = renderablePreviewInfo(designId, previewType)
      const { color, studioUrl, nonPersonalizedStudioUrl } = designById(designId)
      const { colorSwatches } = tileEntityById(entityId)
      const currentColorSwatchId = colorSwatches.find((cs) => cs === designId) as string
      const currentColorSwatch = designById(currentColorSwatchId)

      return {
        ...currentColorSwatch,
        color,
        designId,
        previewType,
        renderablePreviews,
        studioUrl,
        colorSwatches: colorSwatches.map(designById).filter(Boolean),
        nonPersonalizedStudioUrl,
      }
    },
)

export const renderableSimilarPreviewInfoSelector = createSelector(
  stringRenderPropertySelector,
  similarDesignByIdSelector,
  (stringRenderProperty, designById) =>
    (
      currentDesignId: string,
      previewType: PREVIEW_TYPE = PREVIEW_TYPE.TILE,
      previewInfoProp?: Gallery.ContentQuery.PreviewInfo,
    ): Gallery.Designs.RenderablePreviewInfo => {
      const previewInfo = previewInfoProp ?? designById(currentDesignId).previewInfo
      const { previewUrls } = designById(currentDesignId)
      const tilePreviewSize = stringRenderProperty(RenderProperty.TilePreviewSize)
      const imageCacheVersion = stringRenderProperty(RenderProperty.ImageCacheVersion)

      return getPreviewData(previewInfo, previewType, previewUrls, tilePreviewSize, imageCacheVersion)
    },
)

export const similarColorSwatchSelector = createSelector(
  renderableSimilarPreviewInfoSelector,
  similarDesignByIdSelector,
  similarTemplatesByIdSelector,
  (state: State.GlobalState) => state.similarTemplates.designs,
  (renderablePreviewInfo, designById, tileEntityById, designs) =>
    (designId: string, entityId: string, previewType: PREVIEW_TYPE): Gallery.Designs.CurrentColorSwatch => {
      const renderablePreviews = renderablePreviewInfo(designId, previewType)

      const { color, studioUrl } = designById(designId)
      const { colorSwatches } = tileEntityById(entityId)
      const currentColorSwatchId = colorSwatches.find((cs) => cs === designId) as string
      const currentColorSwatch = designs.byId[currentColorSwatchId]

      return {
        ...currentColorSwatch,
        color,
        designId,
        previewType,
        renderablePreviews,
        studioUrl,
        colorSwatches: colorSwatches.map((cs) => designs.byId[cs]).filter(Boolean),
      }
    },
)

export const colorSwatchSelectorWithDesignVariation = createSelector(
  designByIdSelector,
  colorSwatchSelector,
  getLocaleSelector,
  stringRenderPropertySelector,
  getQuantitySelector,
  (designById, colorSwatch, locale, stringRenderProperty, quantity) =>
    (
      designId: string,
      entityId: string,
      designVariation: DesignVariations.DesignVariationResponse | undefined,
      previewType: PREVIEW_TYPE,
      impressionId?: string,
    ): Gallery.Designs.CurrentColorSwatch => {
      const currentColorSwatch = designVariation?.colorSwatches.length
        ? (designVariation?.colorSwatches.find((c) => c.designId === designId) ?? designVariation.colorSwatches[0])
        : ((designVariation || {}) as Gallery.ContentQuery.ColorSwatch)

      if (!currentColorSwatch || !designVariation) {
        try {
          // Hack to handle the case where the design variation is not available and designId is not in Redux state.
          // It happens when user goes back to prev template after selecting template from "More like this" section.
          if (!designById(designId)) {
            return getDefaultColorSwatch(designId, previewType)
          }

          return colorSwatch(designId, entityId, previewType)
        } catch (e) {
          getLogger().error(
            {
              designId,
              entityId,
              designVariation,
              code: 'SD001',
              message: `Current color swatch is null and old designID is being used: ${(e as Error).message}`,
            },
            e as Error,
          )
          throw e
        }
      }
      const { colorSwatches, studioUrl, nonPersonalizedStudioUrl } = designVariation
      const tilePreviewSize = stringRenderProperty(RenderProperty.TilePreviewSize)
      const imageCacheVersion = stringRenderProperty(RenderProperty.ImageCacheVersion)

      const { color, previewUrls, isLightColor, nonPersonalizedPreviewUrls } = currentColorSwatch
      const renderablePreviews = getPreviewData(
        currentColorSwatch ? currentColorSwatch.previewInfo : designVariation.previewInfo,
        PREVIEW_TYPE.QUICKVIEW,
        previewUrls,
        tilePreviewSize,
        imageCacheVersion,
      )

      return {
        color,
        colorSwatches,
        designId,
        previewType,
        renderablePreviews,
        previewUrls,
        isLightColor,
        externalId: currentColorSwatch.externalId,
        designColors: currentColorSwatch.designColors,
        colorComposition: currentColorSwatch.colorComposition,
        previewInfo: currentColorSwatch ? currentColorSwatch.previewInfo : designVariation.previewInfo,
        studioUrl: buildFullyQualifiedVistaprintUrl({
          path: currentColorSwatch ? currentColorSwatch.studioUrl : studioUrl,
          locale,
          quantity,
          impressionId,
        }),
        nonPersonalizedStudioUrl: buildFullyQualifiedVistaprintUrl({
          path: currentColorSwatch ? currentColorSwatch.nonPersonalizedStudioUrl : nonPersonalizedStudioUrl,
          locale,
          quantity,
          impressionId,
        }),
        nonPersonalizedPreviewUrls,
      }
    },
)
